<template>
  <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-labelledby="recordPaymentLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="recordPaymentLabel">
            Record Payment{{ unit && unit.unitNo ? ` - Unit ${unit.unitNo}` : '' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Unit Information -->
          <div v-if="unit" class="mb-3">
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm">
                  <tr>
                    <td><strong>Unit No:</strong></td>
                    <td>{{ unit.unitNo }}</td>
                  </tr>
                  <tr>
                    <td><strong>Occupant:</strong></td>
                    <td>{{ unit.occupants }}</td>
                  </tr>
                  <tr>
                    <td><strong>Phone:</strong></td>
                    <td>{{ unit.phoneNumber }}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-sm">
                  <tr>
                    <td><strong>Monthly Rent:</strong></td>
                    <td>KES {{ numberFormat(unit.monthlyRent) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Current Arrears:</strong></td>
                    <td class="text-danger">KES {{ numberFormat(unit.arrears || 0) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Credit Balance:</strong></td>
                    <td class="text-success">KES {{ numberFormat(unit.creditBalance || 0) }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>

          <!-- Arrears Breakdown -->
          <div v-if="arrearsBreakdown.length > 0" class="mb-3">
            <h6>Arrears Breakdown</h6>
            <div class="table-responsive" style="max-height: 200px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light sticky-top">
                <tr>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Outstanding</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="arrear in arrearsBreakdown" :key="arrear.id">
                  <td>{{ arrear.billing_period }}</td>
                  <td>KES {{ numberFormat(arrear.amount) }}</td>
                  <td>KES {{ numberFormat(arrear.paid_amount || 0) }}</td>
                  <td class="text-danger">KES {{ numberFormat(arrear.outstanding) }}</td>
                  <td>
                      <span class="badge" :class="arrear.status === 'PAID' ? 'bg-success' : 'bg-warning'">
                        {{ arrear.status }}
                      </span>
                  </td>
                </tr>
                </tbody>
                <tfoot class="table-secondary">
                <tr>
                  <th>Total</th>
                  <th>KES {{ numberFormat(summary.totalBilled) }}</th>
                  <th>KES {{ numberFormat(summary.totalPaid) }}</th>
                  <th class="text-danger">KES {{ numberFormat(summary.totalOutstanding) }}</th>
                  <th></th>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Payment Form -->
          <div class="border rounded p-3">
            <h6>Payment Details</h6>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Amount Paid <span class="text-danger">*</span></label>
                  <input v-model.number="paymentData.amountPaid" type="number" class="form-control"
                         placeholder="Enter amount" min="0" step="0.01">
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Payment Mode <span class="text-danger">*</span></label>
                  <select v-model="paymentData.paymentMode" class="form-select">
                    <option value="">Select Mode</option>
                    <option value="CASH">Cash</option>
                    <option value="MPESA">M-Pesa</option>
                    <option value="BANK">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Transaction Code</label>
                  <input v-model="paymentData.transactionCode" type="text" class="form-control"
                         placeholder="e.g., M-Pesa code">
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Payer Name</label>
                  <input v-model="paymentData.payerName" type="text" class="form-control"
                         placeholder="Name of person paying">
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Payer Phone</label>
                  <input v-model="paymentData.payerPhone" type="text" class="form-control"
                         placeholder="Phone number">
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Notes</label>
                  <textarea v-model="paymentData.notes" class="form-control" rows="1"
                            placeholder="Optional notes"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Payments -->
          <div v-if="recentPayments.length > 0" class="mt-3">
            <h6>Recent Payments</h6>
            <div class="table-responsive" style="max-height: 150px; overflow-y: auto;">
              <table class="table table-sm">
                <thead class="table-light">
                <tr>
                  <th>Date</th>
                  <th>Receipt No</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Transaction Code</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="payment in recentPayments" :key="payment.id">
                  <td>{{ payment.formatted_date }}</td>
                  <td>{{ payment.receipt_no }}</td>
                  <td>KES {{ numberFormat(payment.amount_paid) }}</td>
                  <td>{{ payment.payment_mode }}</td>
                  <td>{{ payment.transaction_code || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button @click="recordPayment" type="button" class="btn btn-primary" :disabled="!isFormValid || processing">
            <span v-if="processing">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-check me-2"></i>
              Record Payment
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, rent } from "@/api";

export default {
  name: "RecordPaymentModal",
  props: {
    unit: {
      type: Object,
      default: () => ({})
    },
    modalId: {
      type: String,
      default: "record-payment-modal"
    }
  },
  data() {
    return {
      paymentData: {
        amountPaid: null,
        paymentMode: '',
        transactionCode: '',
        payerName: '',
        payerPhone: '',
        notes: ''
      },
      arrearsBreakdown: [],
      recentPayments: [],
      summary: {
        totalBilled: 0,
        totalPaid: 0,
        totalOutstanding: 0
      },
      processing: false
    };
  },
  computed: {
    isFormValid() {
      return this.paymentData.amountPaid > 0 && this.paymentData.paymentMode;
    }
  },
  watch: {
    unit: {
      handler(newUnit) {
        if (newUnit && newUnit.id) {
          this.loadArrearsBreakdown();
          // Pre-fill payer info
          this.paymentData.payerName = newUnit.occupants || '';
          this.paymentData.payerPhone = newUnit.phoneNumber || '';
        }
      },
      immediate: true
    }
  },
  methods: {
    loadArrearsBreakdown() {
      if (!this.unit || !this.unit.id) return;

      const data = new FormData();
      data.append("function", "getUnitArrearsBreakdown");
      data.append("unitId", this.unit.id);

      execute(data, rent)
          .then((res) => {
            if (res.data.success) {
              this.arrearsBreakdown = res.data.data.arrears || [];
              this.recentPayments = res.data.data.payments || [];
              this.summary = res.data.data.summary || {
                totalBilled: 0,
                totalPaid: 0,
                totalOutstanding: 0
              };
            }
          })
          .catch(() => {
            console.error("Failed to load arrears breakdown");
          });
    },

    recordPayment() {
      if (!this.isFormValid) {
        alert("Please fill in all required fields");
        return;
      }

      this.processing = true;

      const data = new FormData();
      data.append("function", "processRentPayment");
      data.append("unitId", this.unit.id);
      data.append("amountPaid", this.paymentData.amountPaid);
      data.append("paymentMode", this.paymentData.paymentMode);
      data.append("transactionCode", this.paymentData.transactionCode);
      data.append("payerName", this.paymentData.payerName);
      data.append("payerPhone", this.paymentData.payerPhone);
      data.append("notes", this.paymentData.notes);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, rent)
          .then((res) => {
            this.processing = false;
            if (res.data.success) {
              alert(res.data.message);
              this.$emit('payment-recorded', res.data.data);

              // Reset form
              this.paymentData = {
                amountPaid: null,
                paymentMode: '',
                transactionCode: '',
                payerName: this.unit.occupants || '',
                payerPhone: this.unit.phoneNumber || '',
                notes: ''
              };

              // Reload data
              this.loadArrearsBreakdown();

              // Close modal
              const modal = window.bootstrap.Modal.getInstance(document.getElementById(this.modalId));
              if (modal) modal.hide();
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.processing = false;
            alert("Error: " + e.message);
          });
    },

    numberFormat(num) {
      const formatter = new Intl.NumberFormat("en-GB");
      return formatter.format(num || 0);
    }
  }
};
</script>

<style scoped>
.table-sm td, .table-sm th {
  padding: 0.25rem 0.5rem;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
</style>